import { AlertTriangle, Eye, EyeOff, PhoneCall } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { zodResolver } from "@/lib/form";
import { MIN_PASSWORD, tokenFromHash, withArticle } from "@/lib/invitations";
import { landingPath } from "@/lib/nav";
import { ROLE_LABELS, type InvitationPreview } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * The page an invitation's link opens (ADR-028 in `platform`): who invited them and to what, then
 * their name and a password, and they are signed in.
 *
 * The token is the link's fragment, `/invite#<token>`. A browser never sends a fragment to a
 * server, so the token reaches core-api only in the body of the two requests this page makes.
 *
 * Signing in remounts everything under `AuthProvider`, this page included, so the page cannot keep
 * a flag saying it has just accepted. It needs none: a signed-in reader whose address is the
 * invitation's is the person it made, and goes on to their landing page.
 */
export function InvitePage() {
  const { user, loading, logout } = useAuth();
  const { hash } = useLocation();
  const token = tokenFromHash(hash);
  const [attempt, setAttempt] = React.useState(0);
  const preview = usePreview(token, attempt);

  if (loading) return null;

  if (!token) {
    return (
      <Shell>
        <Notice
          title="This link is incomplete"
          body="Open the link from your invitation again. If it still does not work, ask the person who invited you to send it again."
        />
      </Shell>
    );
  }

  if (preview.state === "loading") {
    return (
      <Shell>
        <div className="flex justify-center py-8">
          <Spinner className="size-5" />
        </div>
      </Shell>
    );
  }

  if (preview.state === "failed") {
    const invalid = preview.error instanceof ApiError && preview.error.status === 404;
    return (
      <Shell>
        <Notice
          title={invalid ? "This invitation link is not valid" : "Could not load the invitation"}
          body={
            invalid
              ? "It may have been sent again or deleted. Ask the person who invited you for a new one."
              : preview.error instanceof Error ? preview.error.message : "Check your connection and try again."
          }
          action={
            !invalid && (
              <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </Button>
            )
          }
        />
      </Shell>
    );
  }

  const { invitation } = preview;
  const inviter = invitation.invited_by_name ?? "the person who invited you";

  if (user) {
    if (user.email.toLowerCase() === invitation.email.toLowerCase()) {
      return <Navigate to={landingPath(user.role)} replace />;
    }
    return (
      <Shell>
        <Notice
          title="You are signed in as somebody else"
          body={
            <>
              This invitation is for <span className="font-medium text-text">{invitation.email}</span>,
              and this browser is signed in as{" "}
              <span className="font-medium text-text">{user.email}</span>. Sign out to accept it.
            </>
          }
          action={
            <Button variant="default" onClick={() => void logout()}>
              Sign out
            </Button>
          }
        />
      </Shell>
    );
  }

  if (invitation.status === "expired") {
    return (
      <Shell>
        <Notice
          title="This invitation has expired"
          body={`Invitations work for 72 hours. Ask ${inviter} to send it again.`}
        />
      </Shell>
    );
  }

  if (invitation.status === "accepted") {
    return (
      <Shell>
        <Notice
          title="This invitation has already been used"
          body={`The account for ${invitation.email} exists. Sign in with its email and password.`}
          action={
            <Button variant="default" asChild>
              <Link to="/login">Sign in</Link>
            </Button>
          }
        />
      </Shell>
    );
  }

  return (
    <Shell
      subtitle={
        invitation.invited_by_name
          ? `${invitation.invited_by_name} invited you as ${withArticle(invitation.role)}.`
          : `You are invited as ${withArticle(invitation.role)}.`
      }
    >
      <AcceptForm token={token} invitation={invitation} />
    </Shell>
  );
}

type Preview =
  | { state: "loading" }
  | { state: "ready"; invitation: InvitationPreview }
  | { state: "failed"; error: unknown };

/**
 * The invitation behind the link, kept in this page's own state and not in the account's query
 * cache. Accepting starts a sign-in, which retires that cache and cancels the requests of the
 * session it replaces: a preview kept there went back to loading on submit, took the form away
 * from the person submitting it, and on a refusal came back as "Session operation cancelled".
 */
function usePreview(token: string, attempt: number): Preview {
  const [preview, setPreview] = React.useState<Preview>({ state: "loading" });
  React.useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    setPreview({ state: "loading" });
    api.previewInvitation<InvitationPreview>(token, controller.signal).then(
      (invitation) => setPreview({ state: "ready", invitation }),
      (error: unknown) => {
        if (!controller.signal.aborted) setPreview({ state: "failed", error });
      },
    );
    return () => controller.abort();
  }, [token, attempt]);
  return preview;
}

const schema = z.object({
  display_name: z.string().max(200, "At most 200 characters"),
  password: z.string().min(MIN_PASSWORD, `At least ${MIN_PASSWORD} characters`),
});
type FormValues = z.infer<typeof schema>;

function AcceptForm({ token, invitation }: { token: string; invitation: InvitationPreview }) {
  const { acceptInvitation } = useAuth();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [reveal, setReveal] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { display_name: invitation.display_name ?? "", password: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      // On success the session changes and this page is remounted signed in; see `InvitePage`.
      await acceptInvitation({
        token,
        password: values.password,
        display_name: values.display_name.trim() || null,
      });
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not reach the server. Try again.");
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      {formError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-danger-4 bg-danger-2 px-3 py-2 text-xs text-danger-11"
        >
          <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{formError}</span>
        </div>
      )}

      <dl className="space-y-2 rounded-md border border-border px-3 py-2.5 text-xs">
        <Detail label="Email">
          <span className="break-all">{invitation.email}</span>
        </Detail>
        <Detail label="Role">{ROLE_LABELS[invitation.role]}</Detail>
        {invitation.campaigns.length > 0 && (
          <Detail label="Campaigns">
            <span className="flex flex-wrap gap-1">
              {invitation.campaigns.map((name) => (
                <Badge key={name} tone="neutral" size="sm">
                  {name}
                </Badge>
              ))}
            </span>
          </Detail>
        )}
        {invitation.skills.length > 0 && (
          <Detail label="Skills">
            <span className="flex flex-wrap gap-1">
              {invitation.skills.map((skill) => (
                <Badge key={skill.slug} tone="outline" size="sm">
                  {skill.name} · {skill.level}
                </Badge>
              ))}
            </span>
          </Detail>
        )}
      </dl>

      {/* The address, for a password manager to save the new password against. */}
      <input
        type="email"
        name="username"
        autoComplete="username"
        value={invitation.email}
        readOnly
        hidden
      />

      <Field label="Your name" htmlFor="display_name" hint="How colleagues and the people you call see you.">
        <Input autoComplete="name" {...form.register("display_name")} />
      </Field>

      <Field
        label="Choose a password"
        htmlFor="password"
        hint={`At least ${MIN_PASSWORD} characters. You sign in with it and your email.`}
        error={form.formState.errors.password?.message}
      >
        <Input
          type={reveal ? "text" : "password"}
          autoComplete="new-password"
          autoFocus
          invalid={Boolean(form.formState.errors.password)}
          trailing={
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="rounded p-1 text-text-subtle transition-colors hover:bg-surface-hover hover:text-text"
              aria-label={reveal ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {reveal ? <EyeOff /> : <Eye />}
            </button>
          }
          {...form.register("password")}
        />
      </Field>

      <Button
        type="submit"
        variant="default"
        size="lg"
        className="w-full"
        loading={form.formState.isSubmitting}
      >
        Accept and sign in
      </Button>

      <p className="text-center text-xs text-text-subtle">
        This invitation expires {formatDateTime(invitation.expires_at)}.
      </p>
    </form>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-20 shrink-0 text-text-muted">{label}</dt>
      <dd className="min-w-0 flex-1 text-text">{children}</dd>
    </div>
  );
}

/** The card a page opened from a link sits in, outside the signed-in console. */
export function Shell({
  children, subtitle, title = "Join AI Case Leads",
}: {
  children: React.ReactNode;
  subtitle?: string;
  title?: string;
}) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center text-center">
          <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-accent-9 text-white shadow-sm">
            <PhoneCall className="size-5" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-text-muted">{subtitle}</p>}
        </div>
        <div className="rounded-xl border border-border bg-surface p-5 shadow-sm">{children}</div>
      </div>
    </div>
  );
}

export function Notice({
  title, body, action,
}: {
  title: string;
  body: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="space-y-2 py-2 text-center">
      <p className="text-sm font-medium text-text">{title}</p>
      <p className="text-xs text-text-muted">{body}</p>
      {action && <div className="pt-2">{action}</div>}
    </div>
  );
}
